import { useState, useSyncExternalStore } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import { FigurePreset, Puppet, TherapistFigure } from "../../domain/types";
import { FIGURES_BY_CATEGORY, FigureCategory } from "../../domain/figures";
import { createPuppet } from "../../application/boardReducer";
import {
    getThumbnail,
    subscribeThumbnails,
    thumbnailsSnapshot,
} from "../../application/figureThumbnails";

type Props = {
    onAdd: (puppet: Puppet) => void;
    disabled?: boolean;
    maxNameLength?: number;
    onCreateAvatar?: () => void;
    // Therapist-created figures (DB-backed), shown in their own category.
    therapistFigures?: TherapistFigure[];
    // When true, each therapist figure shows edit/delete affordances.
    canManageFigures?: boolean;
    onEditFigure?: (figure: TherapistFigure) => void;
    onDeleteFigure?: (figure: TherapistFigure) => void;
    // Open the figure picker immediately (e.g. from an immersive "+" button)
    // and hide the inline trigger button. onClose fires when the picker closes.
    startOpen?: boolean;
    hideTrigger?: boolean;
    onClose?: () => void;
    // Portal target for the picker overlay. Pass the immersive/fullscreen
    // container so the modal is a descendant of the OS-fullscreen element and
    // stacks above the board (issue #197). Defaults to document.body.
    portalContainer?: HTMLElement | null;
};

// People, objects, others.
const CATEGORY_ORDER: FigureCategory[] = ["human", "object", "monster"];

export default function PuppetBuilder({
    onAdd,
    disabled,
    maxNameLength = 60,
    onCreateAvatar,
    therapistFigures = [],
    canManageFigures = false,
    onEditFigure,
    onDeleteFigure,
    startOpen = false,
    hideTrigger = false,
    onClose,
    portalContainer,
}: Props) {
    const { t } = useTranslation();
    // re-render as 3D thumbnails finish baking
    useSyncExternalStore(subscribeThumbnails, thumbnailsSnapshot, thumbnailsSnapshot);
    const [open, setOpen] = useState(startOpen);
    const closePicker = () => {
        setOpen(false);
        onClose?.();
    };
    const [name, setName] = useState("");
    const [figure, setFigure] = useState<FigurePreset>(FIGURES_BY_CATEGORY.human[0].key);
    // A selected therapist figure (mutually exclusive with `figure`).
    const [selectedTf, setSelectedTf] = useState<TherapistFigure | null>(null);
    // All categories start collapsed; the user expands what they need.
    const [collapsed, setCollapsed] = useState<Set<FigureCategory>>(() => new Set(CATEGORY_ORDER));
    // The therapist-figures group (not a static FigureCategory) starts collapsed too.
    const [tfCollapsed, setTfCollapsed] = useState(true);
    const toggleCategory = (c: FigureCategory) =>
        setCollapsed((prev) => {
            const next = new Set(prev);
            if (next.has(c)) next.delete(c);
            else next.add(c);
            return next;
        });

    const canAdd = name.trim().length > 0 && !disabled;

    // Preset figures are unnamed and unsearchable, so no label steers which
    // one a representative picks.
    const presets = (category: FigureCategory) =>
        FIGURES_BY_CATEGORY[category].filter(
            (f) => f.key !== FigurePreset.AVATAR, // created via the configurator
        );

    // Pick a saved figure; prefill the name field if it's still empty.
    const selectTherapistFigure = (f: TherapistFigure) => {
        setSelectedTf(f);
        setName((prev) => (prev.trim() ? prev : f.name));
    };

    const handleAdd = () => {
        if (!canAdd) return;
        if (selectedTf) {
            onAdd(
                createPuppet(name.trim(), {
                    figure: FigurePreset.AVATAR,
                    avatar: selectedTf.avatar,
                    ...(selectedTf.height != null ? { height: selectedTf.height } : {}),
                }),
            );
        } else {
            onAdd(createPuppet(name.trim(), { figure }));
        }
        setName("");
        setSelectedTf(null);
        closePicker();
    };

    return (
        <div className={hideTrigger ? "contents" : "p-4"}>
            {/* Trigger: opens the figure picker modal */}
            {!hideTrigger && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    disabled={disabled}
                    className="flex min-h-[44px] w-full items-center justify-center gap-1.5 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-hover disabled:opacity-50"
                >
                    <Plus className="h-4 w-4" />
                    {t("constellation.builder.add")}
                </button>
            )}

            {open && createPortal(
                <div
                    className="fixed inset-0 z-[80] flex items-end justify-center bg-black/30 sm:items-center"
                    onClick={() => closePicker()}
                >
                    <div
                        className="flex max-h-[85vh] w-full max-w-md flex-col gap-3 overflow-hidden rounded-t-2xl bg-white p-5 shadow-xl sm:rounded-2xl"
                        onClick={(e) => e.stopPropagation()}
                        aria-label={t("constellation.builder.pickTitle")}
                    >
                        <div className="flex items-center justify-between">
                            <h2 className="text-base font-semibold">
                                {t("constellation.builder.pickTitle")}
                            </h2>
                            <button
                                type="button"
                                onClick={() => closePicker()}
                                aria-label={t("common.close", "Close")}
                                className="rounded-md px-2 py-1 text-gray-500 hover:bg-gray-100"
                            >
                                ✕
                            </button>
                        </div>

                        <label className="flex flex-col gap-1 text-xs">
                            {t("constellation.builder.name")}
                            <input
                                type="text"
                                value={name}
                                maxLength={maxNameLength}
                                onChange={(e) => setName(e.target.value)}
                                className="rounded-md border px-2 py-1"
                                placeholder={t("constellation.builder.namePlaceholder")}
                            />
                        </label>

                        {onCreateAvatar && (
                            <button
                                type="button"
                                onClick={() => {
                                    closePicker();
                                    onCreateAvatar();
                                }}
                                className="flex min-h-[40px] w-full items-center justify-center gap-1.5 rounded-md border border-fuchsia-300 bg-fuchsia-50 px-3 text-sm font-medium text-fuchsia-900 hover:bg-fuchsia-100"
                            >
                                🧑‍🎨 {t("constellation.avatar.create", "Create avatar")}
                            </button>
                        )}

                        <div className="flex flex-col gap-2 overflow-y-auto text-xs">
                            {therapistFigures.length > 0 && (() => {
                                const tfOpen = !tfCollapsed;
                                return (
                                <div>
                                    <button
                                        type="button"
                                        onClick={() => setTfCollapsed((v) => !v)}
                                        aria-expanded={tfOpen}
                                        className="mb-1 flex w-full items-center gap-1 text-xs font-bold uppercase tracking-wide text-content-tertiary hover:text-gray-600"
                                    >
                                        {tfOpen ? (
                                            <ChevronDown className="h-3 w-3" />
                                        ) : (
                                            <ChevronRight className="h-3 w-3" />
                                        )}
                                        {t("constellation.category.therapistFigures")}
                                        <span className="ml-auto font-normal normal-case text-gray-300">
                                            {therapistFigures.length}
                                        </span>
                                    </button>
                                    {tfOpen && (
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {therapistFigures.map((f) => {
                                            const isSel = selectedTf?.id === f.id;
                                            return (
                                                <div key={f.id} className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => selectTherapistFigure(f)}
                                                        title={f.name}
                                                        aria-label={f.name}
                                                        aria-pressed={isSel}
                                                        className={`flex w-full flex-col items-center gap-0.5 rounded-md border p-1 transition-all ${
                                                            isSel
                                                                ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200"
                                                                : "border-gray-100 hover:border-brand-200"
                                                        }`}
                                                    >
                                                        <div className="flex aspect-square w-full items-center justify-center overflow-hidden text-lg">
                                                            {f.thumbnail ? (
                                                                <img
                                                                    src={f.thumbnail}
                                                                    alt=""
                                                                    aria-hidden
                                                                    className="h-full w-full object-contain"
                                                                />
                                                            ) : (
                                                                <span aria-hidden>🧑‍🎨</span>
                                                            )}
                                                        </div>
                                                        <span className="w-full truncate text-center text-xs leading-tight text-gray-600">
                                                            {f.name}
                                                        </span>
                                                    </button>
                                                    {canManageFigures && (
                                                        <div className="absolute right-0.5 top-0.5 flex gap-0.5">
                                                            <button
                                                                type="button"
                                                                onClick={() => onEditFigure?.(f)}
                                                                aria-label={t("constellation.figureCatalog.edit")}
                                                                title={t("constellation.figureCatalog.edit")}
                                                                className="rounded bg-white/90 p-0.5 text-gray-500 shadow-sm hover:text-brand-600"
                                                            >
                                                                <Pencil className="h-3 w-3" />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => onDeleteFigure?.(f)}
                                                                aria-label={t("constellation.figureCatalog.delete")}
                                                                title={t("constellation.figureCatalog.delete")}
                                                                className="rounded bg-white/90 p-0.5 text-gray-500 shadow-sm hover:text-red-600"
                                                            >
                                                                <Trash2 className="h-3 w-3" />
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                    )}
                                </div>
                                );
                            })()}
                            {CATEGORY_ORDER.map((category) => {
                                const figs = presets(category);
                                if (figs.length === 0) return null;
                                const isOpen = !collapsed.has(category);
                                return (
                                    <div key={category}>
                                        <button
                                            type="button"
                                            onClick={() => toggleCategory(category)}
                                            aria-expanded={isOpen}
                                            className="mb-1 flex w-full items-center gap-1 text-xs font-bold uppercase tracking-wide text-content-tertiary hover:text-gray-600"
                                        >
                                            {isOpen ? (
                                                <ChevronDown className="h-3 w-3" />
                                            ) : (
                                                <ChevronRight className="h-3 w-3" />
                                            )}
                                            {t(`constellation.category.${category}`)}
                                            <span className="ml-auto font-normal normal-case text-gray-300">
                                                {figs.length}
                                            </span>
                                        </button>
                                        {isOpen && (
                                        <div className="grid grid-cols-3 gap-1.5">
                                            {figs.map((f, i) => (
                                                <button
                                                    key={f.key}
                                                    type="button"
                                                    onClick={() => {
                                                        setFigure(f.key);
                                                        setSelectedTf(null);
                                                    }}
                                                    aria-label={`${t("constellation.builder.figure")} ${i + 1}`}
                                                    aria-pressed={figure === f.key && !selectedTf}
                                                    className={`flex flex-col items-center gap-0.5 rounded-md border p-1 transition-all ${
                                                        figure === f.key && !selectedTf
                                                            ? "border-brand-500 bg-brand-50 ring-2 ring-brand-200"
                                                            : "border-gray-100 hover:border-brand-200"
                                                    }`}
                                                >
                                                    <div className="flex aspect-square w-full items-center justify-center overflow-hidden text-lg">
                                                        {getThumbnail(f.key) ? (
                                                            <img
                                                                src={getThumbnail(f.key)}
                                                                alt=""
                                                                aria-hidden
                                                                className="h-full w-full object-contain"
                                                            />
                                                        ) : (
                                                            <span aria-hidden>{f.emoji}</span>
                                                        )}
                                                    </div>
                                                </button>
                                            ))}
                                        </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            type="button"
                            onClick={handleAdd}
                            disabled={!canAdd}
                            className="mt-1 min-h-[44px] rounded-md bg-accent px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-accent-hover disabled:opacity-50"
                        >
                            {t("constellation.builder.confirm")}
                        </button>
                    </div>
                </div>,
                portalContainer ?? document.body,
            )}
        </div>
    );
}
